/**
 * Location Detail Page - Individual city office page
 * /locations/:slug
 */
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import SEO from '@/components/SEO';
import { 
  MapPin, 
  Phone, 
  Mail,
  Clock, 
  ChevronRight,
  ArrowLeft,
  Globe,
  GraduationCap,
  Users,
  CheckCircle2,
  Star,
  MessageCircle,
  Calendar,
  Navigation,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API_URL = window.location.origin || '';

const LocationDetailPage = () => {
  const { slug } = useParams();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [allLocations, setAllLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch this location
        const res = await fetch(`${API_URL}/api/locations/public/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setLocation(data);
        } else {
          navigate('/locations');
          return;
        }
        
        // Fetch all locations for "Other offices" section
        const allRes = await fetch(`${API_URL}/api/locations/public`);
        if (allRes.ok) {
          const allData = await allRes.json();
          setAllLocations(allData);
        }
      } catch (error) {
        console.error('Error fetching location:', error);
        navigate('/locations');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-ux-blue" />
      </div>
    );
  }

  if (!location) {
    return null;
  }

  // Schema.org LocalBusiness structured data
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": `UniXperts ${location.city}${location.area ? ` - ${location.area}` : ''}`,
    "description": location.description,
    "url": `https://unixperts.com/locations/${location.slug}`,
    "telephone": location.phone,
    "email": location.email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": `${location.address.line1}, ${location.address.line2}`,
      "addressLocality": location.address.city,
      "addressRegion": location.address.state,
      "postalCode": location.address.pincode,
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": location.coordinates.lat,
      "longitude": location.coordinates.lng
    },
    "openingHours": "Mo-Sa 10:00-19:00",
    "priceRange": "$$",
    "areaServed": {
      "@type": "City",
      "name": location.city
    },
    "parentOrganization": {
      "@type": "Organization",
      "name": "UniXperts",
      "url": "https://unixperts.com"
    }
  };

  const services = [
    { icon: GraduationCap, title: 'University Selection', desc: 'AI-powered matching with 500+ partner universities' },
    { icon: Globe, title: 'Visa Assistance', desc: '98% visa success rate with expert guidance' },
    { icon: Users, title: 'Free Counseling', desc: 'Personalized one-on-one sessions' },
    { icon: Star, title: 'Scholarships', desc: 'Help secure scholarships worth $50M+' }
  ];

  return (
    <>
      <SEO 
        title={`Study Abroad Consultants in ${location.city}${location.area ? ` ${location.area}` : ''} - ${location.state} | UniXperts`}
        description={location.description || `Visit UniXperts ${location.city} for expert study abroad guidance. Free counseling for USA, UK, Canada, Australia admissions. Call ${location.phone}. Open Mon-Sat 10AM-7PM.`}
        keywords={location.keywords || `study abroad consultants ${location.city}, overseas education ${location.city}, USA university admission ${location.city}, UK student visa ${location.city}, Canada PR ${location.city}, IELTS coaching ${location.city}, UniXperts ${location.city}`}
        canonical={`https://unixperts.com/locations/${location.slug}`}
        structuredData={localBusinessSchema}
      />
      
      {/* Inject LocalBusiness Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }} />

      <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
        {/* Hero Section */}
        <section className={`py-12 md:py-20 ${isDark ? 'bg-gradient-to-br from-[#1a2830] to-[#0d1117]' : 'bg-gradient-to-br from-ux-blue to-ux-cyan'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Link */}
            <Link 
              to="/locations" 
              className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All Locations
            </Link>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-white/20 text-white mb-4">
                  {location.state}
                </Badge>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 font-display">
                  Study Abroad Consultants in {location.city}
                  {location.area && <span className="text-white/80 text-3xl block mt-1">({location.area})</span>}
                </h1>
                <p className="text-xl text-white/80 mt-4 mb-6">
                  {location.tagline}
                </p>
                <p className="text-white/70 mb-8">
                  {location.description}
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    className="bg-white text-ux-blue hover:bg-white/90"
                    asChild
                  >
                    <Link to="/contact">
                      <Calendar className="w-4 h-4 mr-2" />
                      Book Free Consultation
                    </Link>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="hero-outline"
                    asChild
                  >
                    <a href={`tel:${location.phone}`}>
                      <Phone className="w-4 h-4 mr-2" />
                      {location.phone}
                    </a>
                  </Button>
                </div>
              </div>

              {/* Office Info Card */}
              <div className={`p-6 rounded-2xl ${isDark ? 'bg-white/10 backdrop-blur-sm' : 'bg-white/20 backdrop-blur-sm'}`}>
                <h2 className="text-xl font-bold text-white mb-4">Visit Our Office</h2>
                <div className="space-y-4 text-white/90">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-medium">{location.address.line1}</p>
                      <p>{location.address.line2}</p>
                      <p>{location.address.city}, {location.address.state} - {location.address.pincode}</p>
                      <p className="text-white/60 text-sm mt-1">{location.address.landmark}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 flex-shrink-0" />
                    <span>{location.timings}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 flex-shrink-0" />
                    <a href={`mailto:${location.email}`} className="hover:underline">{location.email}</a>
                  </div>
                  <Button 
                    className="w-full mt-4 bg-white/20 hover:bg-white/30 text-white"
                    asChild
                  >
                    <a href={location.map_url} target="_blank" rel="noopener noreferrer">
                      <Navigation className="w-4 h-4 mr-2" />
                      Get Directions
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className={`text-3xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Why Choose UniXperts in {location.city}?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className={`text-lg mb-6 ${isDark ? 'text-white/80' : 'text-slate-600'}`}>
                  {location.unique_content}
                </p>
                
                <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  We Specialize In:
                </h3>
                <ul className="space-y-3">
                  {(location.local_focus || []).map((focus, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-ux-cyan flex-shrink-0 mt-0.5" />
                      <span className={isDark ? 'text-white/80' : 'text-slate-600'}>{focus}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Popular Destinations from {location.city}
                </h3>
                <div className="flex flex-wrap gap-3 mb-8">
                  {(location.popular_destinations || []).map(dest => (
                    <Link 
                      key={dest}
                      to={`/destinations/${dest.toLowerCase()}`}
                      className={`px-4 py-2 rounded-full font-medium transition-colors ${
                        isDark 
                          ? 'bg-white/10 text-white hover:bg-white/20' 
                          : 'bg-ux-blue/10 text-ux-blue hover:bg-ux-blue/20'
                      }`}
                    >
                      Study in {dest}
                    </Link>
                  ))}
                </div>

                {/* Google Map Embed Placeholder */}
                <div className={`aspect-video rounded-xl overflow-hidden ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}>
                  <iframe 
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(location.address.line2 + ', ' + location.address.city)}`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`UniXperts ${location.city} Office Location`}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className={`py-16 ${isDark ? 'bg-white/5' : 'bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className={`text-3xl font-bold mb-8 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Our Services in {location.city}
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, idx) => (
                <Card key={idx} className={`text-center ${isDark ? 'bg-white/5 border-white/10' : ''}`}>
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center ${
                      isDark ? 'bg-ux-cyan/20' : 'bg-ux-blue/10'
                    }`}>
                      <service.icon className={`w-6 h-6 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
                    </div>
                    <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {service.title}
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                      {service.desc}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-8">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white"
                asChild
              >
                <Link to="/services">
                  View All Services
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className={`overflow-hidden ${isDark ? 'bg-gradient-to-br from-ux-blue/20 to-ux-cyan/20 border-white/10' : 'bg-gradient-to-br from-ux-blue to-ux-cyan'}`}>
              <CardContent className="p-8 md:p-12 text-center">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 text-white" />
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Ready to Start Your Journey?
                </h2>
                <p className="text-white/80 mb-8 max-w-xl mx-auto">
                  Book a free consultation at our {location.city} office. Our expert counselors will help you choose the right university and guide you through every step.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg"
                    className="bg-white text-ux-blue hover:bg-white/90"
                    asChild
                  >
                    <Link to="/contact">
                      <Calendar className="w-4 h-4 mr-2" />
                      Book Appointment
                    </Link>
                  </Button>
                  <Button 
                    size="lg"
                    variant="hero-outline"
                    asChild
                  >
                    <a href={`tel:${location.phone}`}>
                      <Phone className="w-4 h-4 mr-2" />
                      Call Now
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Other Locations */}
        <section className={`py-16 ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Other UniXperts Offices
            </h2>
            <div className="flex flex-wrap gap-3">
              {allLocations.filter(loc => loc.slug !== slug).map(loc => (
                <Link
                  key={loc.id}
                  to={`/locations/${loc.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isDark 
                      ? 'bg-white/10 text-white/80 hover:bg-white/20' 
                      : 'bg-white text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {loc.city}{loc.area ? ` (${loc.area})` : ''}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LocationDetailPage;
